import { create } from "zustand";
import { listConversationHistory } from "@/api/conversations";
import type { SpaceMessage } from "@/api/spaces";
import { hydrateConversationMessages } from "@/desktop/conversation-hydration";
import { useConversationStore } from "@/stores/conversation-store";

const CONVERSATION_HISTORY_LIMIT = 500;
const inFlightHistoryHydrations = new Map<string, Promise<void>>();
let historyHydrationGeneration = 0;

// Live registry of binding state used by conversation-frame to choose
// between the cold empty-state and the loading placeholder.
//
// `hydrating` — binding keys currently in flight.
// `settled`   — binding keys whose hydration cycle has finished (success
//               OR failure). Settling on failure too means the loading
//               placeholder collapses into the cold empty-state instead
//               of hanging forever; the next hydrate call will re-fetch
//               because `hasCachedHistory` also requires visible messages
//               and a failed cycle leaves the conversation store empty.
//
// Both Sets live in the same zustand store so React components subscribe
// reactively to either signal.
interface HistoryHydrationState {
  hydrating: ReadonlySet<string>;
  settled: ReadonlySet<string>;
  startHydration: (key: string) => void;
  endHydration: (key: string) => void;
  reset: () => void;
}

const useHistoryHydrationStore = create<HistoryHydrationState>((set) => ({
  hydrating: new Set<string>(),
  settled: new Set<string>(),
  startHydration: (key) =>
    set((state) => {
      if (state.hydrating.has(key)) return state;
      const next = new Set(state.hydrating);
      next.add(key);
      return { hydrating: next };
    }),
  endHydration: (key) =>
    set((state) => {
      const hydrating = state.hydrating.has(key)
        ? (() => {
            const next = new Set(state.hydrating);
            next.delete(key);
            return next;
          })()
        : state.hydrating;
      const settled = state.settled.has(key)
        ? state.settled
        : (() => {
            const next = new Set(state.settled);
            next.add(key);
            return next;
          })();
      if (hydrating === state.hydrating && settled === state.settled) {
        return state;
      }
      return { hydrating, settled };
    }),
  reset: () =>
    set((state) => {
      if (state.hydrating.size === 0 && state.settled.size === 0) {
        return state;
      }
      return { hydrating: new Set<string>(), settled: new Set<string>() };
    }),
}));

/** A frame's binding, or a conversation alone (an input bar pick, which has
 *  no frame and may live in any project). */
interface ConversationFrameHistoryArgs {
  projectId?: string;
  spaceId?: string;
  frameId?: string;
  conversationId?: string;
  refresh?: boolean;
  shouldContinue?: () => boolean;
}

function canContinue(args: { shouldContinue?: () => boolean }) {
  return args.shouldContinue?.() ?? true;
}

function historyKey(args: ConversationFrameHistoryArgs): string {
  const binding = args.conversationId
    ? `conversation:${args.conversationId}`
    : "unbound";
  return `${args.projectId ?? ""}:${args.spaceId ?? ""}:${args.frameId ?? ""}:${binding}`;
}

function hasVisibleHistory(args: ConversationFrameHistoryArgs): boolean {
  return useConversationStore.getState().messages.some((message) => {
    return (
      !!args.conversationId && message.conversationId === args.conversationId
    );
  });
}

async function listRecentConversationMessages(
  conversationId: string,
): Promise<SpaceMessage[]> {
  const [messages, activity] = await Promise.all([
    listRecentHistoryStream(conversationId, "messages"),
    listRecentHistoryStream(conversationId, "activity"),
  ]);
  return [...messages, ...activity]
    .sort((left, right) => {
      const byCreatedAt = left.created_at.localeCompare(right.created_at);
      return byCreatedAt || left.id.localeCompare(right.id);
    })
    .slice(-CONVERSATION_HISTORY_LIMIT);
}

async function listRecentHistoryStream(
  conversationId: string,
  stream: "messages" | "activity",
): Promise<SpaceMessage[]> {
  const pages: SpaceMessage[][] = [];
  let cursor: string | undefined;
  let remaining = CONVERSATION_HISTORY_LIMIT;
  while (remaining > 0) {
    const page = await listConversationHistory(conversationId, {
      cursor: stream === "messages" ? cursor : undefined,
      activityCursor: stream === "activity" ? cursor : undefined,
      limit: Math.min(100, remaining),
      includeMessages: stream === "messages",
      includeActivity: stream === "activity",
    });
    const batch = stream === "messages" ? page.data : (page.activity ?? []);
    const pagination =
      stream === "messages"
        ? page.pagination
        : (page.activity_pagination ?? {
            has_more: false,
            next_cursor: null,
          });
    pages.unshift(batch);
    remaining -= batch.length;
    cursor = pagination.next_cursor ?? undefined;
    if (!pagination.has_more || !cursor || batch.length === 0) break;
  }
  return pages.flat();
}

function hasCachedHistory(args: ConversationFrameHistoryArgs): boolean {
  const key = historyKey(args);
  // A previous cycle may have settled without populating the store (network
  // failure, or cancellation by shouldContinue). Require BOTH the settled
  // flag and visible messages so the next call re-fetches in that case.
  return (
    useHistoryHydrationStore.getState().settled.has(key) &&
    hasVisibleHistory(args)
  );
}

async function runHistoryHydration(
  args: ConversationFrameHistoryArgs,
  generation: number,
): Promise<void> {
  const isCurrent = () => generation === historyHydrationGeneration;
  if (
    !args.conversationId ||
    !isCurrent() ||
    !canContinue(args) ||
    (!args.refresh && hasCachedHistory(args))
  ) {
    return;
  }
  const apiMessages = await listRecentConversationMessages(args.conversationId);
  if (!isCurrent() || !canContinue(args)) return;
  const messages = await hydrateConversationMessages(apiMessages);
  if (!isCurrent() || !canContinue(args)) return;
  useConversationStore.getState().hydrateMessages(
    messages.map((message) => ({
      ...message,
      conversationId: args.conversationId ?? message.conversationId,
      conversationFrameId: message.conversationFrameId ?? args.frameId,
    })),
  );
}

export async function hydrateConversationFrameHistory(
  args: ConversationFrameHistoryArgs,
): Promise<void> {
  if (
    !args.conversationId ||
    !canContinue(args) ||
    (!args.refresh && hasCachedHistory(args))
  ) {
    return;
  }
  const key = historyKey(args);
  const generation = historyHydrationGeneration;
  const inFlight = inFlightHistoryHydrations.get(key);
  if (inFlight) {
    if (!args.refresh) return inFlight;
    // An explicit reopen owns a new reveal guard. The earlier request may
    // discard its result when that guard is cancelled, so fetch for this one.
    await inFlight.catch(() => {});
    if (generation !== historyHydrationGeneration || !canContinue(args)) return;
    return hydrateConversationFrameHistory(args);
  }
  useHistoryHydrationStore.getState().startHydration(key);
  // `endHydration` runs on BOTH success and failure so the loading
  // placeholder always collapses; the cold empty-state shows if the cycle
  // produced no visible messages.
  const hydration = runHistoryHydration(args, generation).finally(() => {
    if (inFlightHistoryHydrations.get(key) === hydration) {
      inFlightHistoryHydrations.delete(key);
    }
    if (generation === historyHydrationGeneration) {
      useHistoryHydrationStore.getState().endHydration(key);
    }
  });
  inFlightHistoryHydrations.set(key, hydration);
  return hydration;
}

export function isConversationFrameHistoryHydrating(
  args: ConversationFrameHistoryArgs,
): boolean {
  return useHistoryHydrationStore.getState().hydrating.has(historyKey(args));
}

export function useIsConversationFrameHistoryHydrating(
  args: ConversationFrameHistoryArgs,
): boolean {
  const key = historyKey(args);
  return useHistoryHydrationStore((s) => s.hydrating.has(key));
}

// True once the binding's hydration cycle has settled at least once.
// Backed by the same zustand store as `useIsConversationFrameHistoryHydrating`
// so the hook variant below re-renders reactively when the flag flips.
export function hasConversationFrameHistoryBeenHydrated(
  args: ConversationFrameHistoryArgs,
): boolean {
  return useHistoryHydrationStore.getState().settled.has(historyKey(args));
}

export function useHasConversationFrameHistoryBeenHydrated(
  args: ConversationFrameHistoryArgs,
): boolean {
  const key = historyKey(args);
  return useHistoryHydrationStore((s) => s.settled.has(key));
}

// Called wherever the conversation store is cleared (an account change).
// Without this reset, a binding hydrated before would see
// a "settled" key for messages just wiped from the conversation store — the
// frame would render the cold empty-state for a tick before the hook
// re-triggered hydration.
export function resetConversationFrameHistoryCache(): void {
  historyHydrationGeneration += 1;
  inFlightHistoryHydrations.clear();
  useHistoryHydrationStore.getState().reset();
}
